import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../utils/authService';
import emailService from '../../utils/emailService';
import { supabase } from '../../utils/supabase';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          setStatus('error');
          setMessage(errorDescription || 'Authentication failed. Please try again.');
          return;
        }

        // Handle email verification
        const token = searchParams.get('token');
        const email = searchParams.get('email');
        const type = searchParams.get('type');

        if (type === 'signup' || (token && email)) {
          // Email verification flow - custom email verification
          const result = await authService.verifyEmail(token, email);
          
          if (result.success) {
            setStatus('success');
            setMessage('Email verified successfully! Redirecting to dashboard...');
            
            // Send welcome email only after successful verification
            try {
              // Get user profile
              const userProfile = await authService.getUserProfile(token); // token is the user ID in this case
              
              // Only send welcome email once
              if (userProfile.data && !userProfile.data.welcome_email_sent) {
                await emailService.sendWelcomeEmail(
                  email,
                  userProfile.data.full_name || email.split('@')[0],
                  userProfile.data.role || 'va'
                );
                
                // Mark that welcome email was sent
                await supabase
                  .from('user_profiles')
                  .update({ welcome_email_sent: true })
                  .eq('id', token);
              }
            } catch (emailError) {
              console.error('Failed to send welcome email:', emailError);
            }
            
            // Redirect to dashboard after delay
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 2000);
          } else {
            setStatus('error');
            setMessage(result.error || 'Email verification failed.');
          }
          return;
        }

        // Handle password reset - DEPRECATED: Now using custom token-based system
        if (type === 'recovery') {
          setStatus('error');
          setMessage('This password reset link is no longer valid. Please request a new password reset from the login page.');
          setTimeout(() => {
            navigate('/login-register', { replace: true });
          }, 3000);
          return;
        }

        // Handle general auth callback (from OAuth or Supabase email confirmation)
        const { data, error: authError } = await supabase.auth.getSession();
        
        if (authError) {
          setStatus('error');
          setMessage('Authentication error. Please try signing in again.');
          return;
        }

        if (data?.session?.user) {
          // User is authenticated via Supabase native email confirmation
          // Ensure the user profile is marked as verified
          try {
            const userId = data.session.user.id;
            const email = data.session.user.email;
            
            // Check if profile exists and update verification status
            const { data: profileData, error: profileError } = await supabase
              .from('user_profiles')
              .select('is_verified')
              .eq('id', userId)
              .single();
              
            if (!profileError && profileData && !profileData.is_verified) {
              // Update profile to mark as verified
              await supabase
                .from('user_profiles')
                .update({ 
                  is_verified: true,
                  updated_at: new Date().toISOString() 
                })
                .eq('id', userId);
            }
            
            // Send welcome email if this is first-time verification
            if (profileData && !profileData.is_verified) {
              try {
                const { data: fullProfile } = await supabase
                  .from('user_profiles')
                  .select('full_name, role, welcome_email_sent')
                  .eq('id', userId)
                  .single();
                  
                if (fullProfile && !fullProfile.welcome_email_sent) {
                  await emailService.sendWelcomeEmail(
                    email,
                    fullProfile.full_name || email.split('@')[0],
                    fullProfile.role || 'va'
                  );
                  
                  await supabase
                    .from('user_profiles')
                    .update({ welcome_email_sent: true })
                    .eq('id', userId);
                }
              } catch (emailError) {
                console.error('Failed to send welcome email:', emailError);
              }
            }
          } catch (syncError) {
            console.error('Error syncing verification status:', syncError);
          }
          
          setStatus('success');
          setMessage('Email verified successfully! Redirecting to dashboard...');
          
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          setMessage('No active session found. Please sign in again.');
        }
        
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate, signIn]);

  const handleRetry = () => {
    navigate('/login-register', { replace: true });
  };

  const handleResendVerification = async () => {
    const email = searchParams.get('email');
    if (email) {
      try {
        await authService.resendVerificationEmail(email);
        setMessage('Verification email resent. Please check your inbox.');
      } catch {
        setMessage('Failed to resend verification email. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-surface rounded-xl shadow-lg border border-border p-8">
          {/* Status Icon */}
          <div className="mb-6">
            {status === 'processing' && (
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            
            {status === 'success' && (
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            
            {status === 'error' && (
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>

          {/* Status Message */}
          <h1 className="text-xl font-bold text-text-primary mb-2">
            {status === 'processing' && 'Processing...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Oops!'}
          </h1>
          
          <p className="text-text-secondary mb-6">
            {message}
          </p>

          {/* Action Buttons */}
          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Back to Sign In
              </button>
              
              {searchParams.get('email') && (
                <button
                  onClick={handleResendVerification}
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Resend Verification Email
                </button>
              )}
            </div>
          )}

          {status === 'processing' && (
            <div className="text-xs text-text-muted">
              This may take a few moments...
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-text-muted">
            Having trouble? Contact us at{' '}
            <a href="mailto:support@vasprouts.com" className="text-primary hover:text-primary-600">
              support@vasprouts.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;


